import React, { useEffect, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { format } from 'date-fns';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { CapacityStatus, PredictPatient, WorkflowStage } from '~/types/predict';
import EhrStatusRow from './EhrStatusRow';
import CapacityTab, { CAPACITY_STATUS_META } from './CapacityTab';

const fmt = (iso: string) => format(new Date(iso), "MMM d, yyyy 'at' h:mm a");

// Drop a leading title (Dr./Mr./…) and trailing credentials (", MSW", ", RN", …).
const displayName = (name: string) =>
  name.replace(/^(Dr|Mr|Mrs|Ms|Dr)\.?\s+/i, '').replace(/,\s*[A-Za-z.]+$/, '').trim();

const verifiedDetail = (s?: { verified: boolean; verifiedAt?: string; verifiedBy?: string }): React.ReactNode => {
  if (!s?.verifiedAt) {
    return undefined;
  }
  const ts = fmt(s.verifiedAt);
  return s.verifiedBy ? <>Verified by <strong>{displayName(s.verifiedBy)}</strong> · {ts}</> : `Verified ${ts}`;
};

// The capacity check, collapsed to a one-line result + timestamp; the full
// capacity UI (template picker, admit slots, ongoing capacity) expands on click.
const CapacityQualifyRow: React.FC<{ patient: PredictPatient }> = ({ patient }) => {
  const [open, setOpen] = useState(false);
  const check = patient.capacityCheck;
  const meta = check ? CAPACITY_STATUS_META[check.overallStatus] : null;

  return (
    <Box>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer' }}
      >
        {meta ? (
          <meta.Icon sx={{ color: meta.color, fontSize: 24, mt: '1px' }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 24, mt: '1px' }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>Program capacity verified</Typography>
            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {check && meta ? `${meta.label} · Last checked ${fmt(check.checkedAt)}` : 'Not yet checked'}
          </Typography>
        </Box>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pl: '36px', pt: 1.5 }}>
          <CapacityTab patient={patient} />
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Qualify stage content — three EHR-driven verification statuses plus the
 * program capacity check (collapsed to its top-level result, details on click).
 */
const QualifyStageContent: React.FC<{ patient: PredictPatient }> = ({ patient }) => {
  const { transitionWorkflow } = usePredictContext();
  const q = patient.qualifyChecks;

  // All four qualify items must clear: the three EHR verifications plus the
  // capacity check. A "caution" result (LIMITED) is allowed to proceed; only
  // a missing check or "No capacity" (UNAVAILABLE) blocks advancing.
  const capacityStatus = patient.capacityCheck?.overallStatus;
  const capacityCleared =
    capacityStatus === CapacityStatus.AVAILABLE || capacityStatus === CapacityStatus.LIMITED;
  const allVerified =
    !!q?.clinicalStability?.verified &&
    !!q?.socialStability?.verified &&
    !!q?.consent?.verified &&
    capacityCleared;

  // Only offer to advance while the patient is still in the Qualify phase.
  const inQualify =
    patient.currentStage === WorkflowStage.ELIGIBILITY || patient.currentStage === WorkflowStage.ACCEPTANCE;

  // Auto-advance to Transfer once every qualification check is green.
  useEffect(() => {
    if (inQualify && allVerified) {
      transitionWorkflow(patient.id, WorkflowStage.TRANSITION, 'Qualification complete — advanced to Transfer');
    }
  }, [inQualify, allVerified, patient.id]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CapacityQualifyRow patient={patient} />
      <EhrStatusRow
        done={!!q?.clinicalStability?.verified}
        title="Clinical stability verified"
        detail={verifiedDetail(q?.clinicalStability)}
      />
      <EhrStatusRow
        done={!!q?.socialStability?.verified}
        title="Social stability verified"
        detail={verifiedDetail(q?.socialStability)}
      />
      <EhrStatusRow
        done={!!q?.consent?.verified}
        title="Patient/family consent received"
        detail={verifiedDetail(q?.consent)}
      />
    </Box>
  );
};

export default QualifyStageContent;
